import React, { useEffect, useState } from "react";
import { Calculator, TrendingUp, TrendingDown } from "lucide-react";
import api, { TRY } from "@/lib/api";
import { Card } from "@/components/Cards";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

export default function PriceSimulator({ customers }) {
  const [target, setTarget] = useState("ALL");
  const [pct, setPct] = useState(10);
  const [newPrice, setNewPrice] = useState("");
  const [data, setData] = useState(null);

  useEffect(() => {
    const params = { customer_id: target, pct: parseFloat(pct) || 0 };
    if (target !== "ALL" && newPrice !== "") params.new_price = parseFloat(newPrice);
    const t = setTimeout(() => api.get("/reports/price-simulation", { params }).then((r) => setData(r.data)).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [target, pct, newPrice]);

  const Delta = ({ v, suffix = "" }) => (
    <span className={`font-mono font-semibold inline-flex items-center gap-1 ${v >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
      {v >= 0 ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />}{v >= 0 ? "+" : ""}{suffix ? `${v.toFixed(1)}${suffix}` : TRY(v)}
    </span>
  );

  return (
    <Card className="p-5" data-testid="price-simulator">
      <div className="flex items-center gap-2 mb-1"><Calculator className="h-5 w-5 text-primary" /><h3 className="font-heading text-lg font-semibold">Fiyat Artışı Simülasyonu</h3></div>
      <p className="text-xs text-muted-foreground mb-4">Son 30 günün kişi sayılarıyla, fiyat değişikliğinin aylık ciro ve kâra etkisi (bu ayın maliyetleri baz alınır). Hiçbir veri değişmez.</p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
        <Select value={target} onValueChange={(v) => { setTarget(v); setNewPrice(""); }}>
          <SelectTrigger className="h-11" data-testid="sim-customer"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="ALL">Tüm müşteriler</SelectItem>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.short_name || c.name}</SelectItem>)}</SelectContent>
        </Select>
        <div className="relative"><Input type="number" step="0.5" value={pct} onChange={(e) => { setPct(e.target.value); setNewPrice(""); }} className="h-11 font-mono pr-14" data-testid="sim-pct" />
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">% artış</span></div>
        <div className="relative"><Input type="number" step="0.5" value={newPrice} disabled={target === "ALL"} onChange={(e) => setNewPrice(e.target.value)} placeholder={target === "ALL" ? "Müşteri seçin" : "veya yeni fiyat"} className="h-11 font-mono pr-8" data-testid="sim-new-price" />
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">₺</span></div>
      </div>

      {data && (<>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
          <Stat label="Aylık Ciro" a={TRY(data.current_revenue)} b={TRY(data.new_revenue)} d={<Delta v={data.delta} />} testId="sim-revenue" />
          <Stat label="Net Kâr" a={TRY(data.current_profit)} b={TRY(data.new_profit)} d={<Delta v={data.new_profit - data.current_profit} />} testId="sim-profit" />
          <Stat label="Kâr Marjı" a={`%${data.current_margin.toFixed(1)}`} b={`%${data.new_margin.toFixed(1)}`} d={<Delta v={data.new_margin - data.current_margin} suffix=" puan" />} testId="sim-margin" />
          <Stat label="Aylık Maliyet (sabit)" a={TRY(data.month_costs)} b="—" d={null} testId="sim-costs" />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="sim-table">
            <thead><tr className="border-b border-border bg-muted/40 text-left">
              {["Müşteri", "Kişi (30g)", "Mevcut Fiyat", "Yeni Fiyat", "Mevcut Ciro", "Yeni Ciro", "Fark"].map((h, i) => <th key={h} className={`px-3 py-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground ${i > 0 ? "text-right" : ""}`}>{h}</th>)}
            </tr></thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.customer_id} data-testid={`sim-row-${r.customer_id}`} className={`border-b border-border/60 ${r.affected ? "" : "opacity-50"}`}>
                  <td className="px-3 py-2 font-medium">{r.name}</td>
                  <td className="px-3 py-2 text-right font-mono">{r.headcount.toLocaleString("tr-TR")}</td>
                  <td className="px-3 py-2 text-right font-mono">{TRY(r.current_price)}</td>
                  <td className={`px-3 py-2 text-right font-mono font-semibold ${r.affected ? "text-primary" : ""}`}>{TRY(r.new_price)}</td>
                  <td className="px-3 py-2 text-right font-mono">{TRY(r.current_revenue)}</td>
                  <td className="px-3 py-2 text-right font-mono">{TRY(r.new_revenue)}</td>
                  <td className="px-3 py-2 text-right">{r.affected ? <Delta v={r.delta} /> : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </>)}
    </Card>
  );
}

const Stat = ({ label, a, b, d, testId }) => (
  <div className="rounded-xl border border-border bg-muted/30 p-3" data-testid={testId}>
    <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</div>
    <div className="mt-1 text-xs text-muted-foreground font-mono">{a} →</div>
    <div className="font-mono font-bold">{b}</div>
    {d && <div className="text-xs mt-0.5">{d}</div>}
  </div>
);
